import React, { useMemo, useRef, useState } from 'react';
import {
 Box,
 Button,
 Divider,
 Flex,
 Input,
 Popover,
 PopoverBody,
 PopoverContent,
 PopoverTrigger,
 Portal,
 Text,
 useDisclosure,
} from '@chakra-ui/react';
import { IoMdArrowDropdown } from 'react-icons/io';

/**
 * Dropdown com busca.
 *
 * Popover e não Menu de propósito: o Menu do Chakra tem typeahead e devolve o
 * foco para os MenuItem a cada tecla, então digitar num Input dentro dele é
 * instável (era o "dropdown que não abre/bugado"). Também não usa datalist, que
 * só sugere o que casa com o texto já digitado — depois de escolher uma opção
 * a lista mostrava só ela.
 */
export function FilterCombobox({
 label,
 value,
 options = [],
 onChange,
 placeholder = 'Todos',
 searchPlaceholder = 'Buscar',
 emptyLabel = 'Nenhuma opção encontrada',
 clearLabel = 'Todos',
 actions = [],
 isLoading = false,
 isDisabled = false,
 size = 'md',
}) {
 const { isOpen, onOpen, onClose } = useDisclosure();
 const [search, setSearch] = useState('');
 const searchRef = useRef(null);

 const normalizedOptions = useMemo(
  () =>
   options.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option
   ),
  [options]
 );

 const filteredOptions = useMemo(() => {
  const term = search.trim().toLowerCase();
  if (!term) return normalizedOptions;
  return normalizedOptions.filter(
   (option) =>
    option.label?.toLowerCase().includes(term) ||
    option.description?.toLowerCase().includes(term)
  );
 }, [normalizedOptions, search]);

 const selectedLabel =
  normalizedOptions.find((option) => option.value === value)?.label || '';

 function close() {
  setSearch('');
  onClose();
 }

 function handleSelect(nextValue) {
  onChange(nextValue);
  close();
 }

 const isSmall = size === 'sm';

 return (
  <Box>
   {!!label && (
    <Text fontSize='xs' color='#818A8F' mb='8px' lineHeight='14.88px' whiteSpace='nowrap'>
     {label}
    </Text>
   )}

   <Popover
    isOpen={isOpen}
    onOpen={onOpen}
    onClose={close}
    initialFocusRef={searchRef}
    placement='bottom-start'
    matchWidth
    isLazy
    // Na coluna da tabela o painel abria de lado: o TableContainer tem
    // overflow-x, então o popover ficava preso nele e era empurrado/cortado.
    // `fixed` + Portal tiram o painel do contexto de rolagem.
    strategy='fixed'
   >
    <PopoverTrigger>
     <Button
      w='100%'
      h={isSmall ? '32px' : '48px'}
      px='12px'
      bg='white'
      border='1px solid #B8CAE8'
      borderRadius='8px'
      fontSize='sm'
      fontWeight={isSmall ? 500 : 600}
      color={selectedLabel ? 'black.100' : '#818A8F'}
      _hover={{ bg: 'white' }}
      _active={{ bg: 'white' }}
      isLoading={isLoading}
      isDisabled={isDisabled}
      rightIcon={<IoMdArrowDropdown color='#292B30' opacity={0.5} fontSize='22px' />}
     >
      <Text flex='1' textAlign='left' noOfLines={1}>
       {selectedLabel || placeholder}
      </Text>
     </Button>
    </PopoverTrigger>

    {/* Sem largura própria: o `matchWidth` alinha o painel ao botão — com
        `w='100%'` no Portal ele viraria a largura do body. */}
    <Portal>
     <PopoverContent borderRadius='8px' _focusVisible={{ outline: 'none' }}>
      <PopoverBody p='8px'>
       <Input
        ref={searchRef}
        size='sm'
        borderRadius='6px'
        placeholder={searchPlaceholder}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        mb='8px'
       />

       <Box maxH='260px' overflowY='auto'>
        {actions.map((action) => (
         <OptionButton
          key={action.label}
          onClick={() => {
           action.onSelect();
           close();
          }}
         >
          {action.label}
         </OptionButton>
        ))}
        {actions.length > 0 && <Divider my='6px' />}

        {!!clearLabel && (
         <OptionButton isSelected={!value} onClick={() => handleSelect('')}>
          {clearLabel}
         </OptionButton>
        )}

        {filteredOptions.map((option) => (
         <OptionButton
          key={option.value}
          isSelected={option.value === value}
          onClick={() => handleSelect(option.value)}
         >
          <Flex direction='column' align='flex-start'>
           <Text fontSize='sm'>{option.label}</Text>
           {!!option.description && (
            <Text fontSize='xs' color='#818A8F'>
             {option.description}
            </Text>
           )}
          </Flex>
         </OptionButton>
        ))}

        {filteredOptions.length === 0 && (
         <Text fontSize='sm' color='#818A8F' px='8px' py='12px'>
          {emptyLabel}
         </Text>
        )}
       </Box>
      </PopoverBody>
     </PopoverContent>
    </Portal>
   </Popover>
  </Box>
 );
}

function OptionButton({ children, isSelected, onClick }) {
 return (
  <Box
   as='button'
   type='button'
   w='100%'
   textAlign='left'
   px='8px'
   py='6px'
   borderRadius='6px'
   fontSize='sm'
   bg={isSelected ? '#EAF2FB' : 'transparent'}
   color={isSelected ? '#0073BB' : 'inherit'}
   fontWeight={isSelected ? 600 : 400}
   _hover={{ bg: isSelected ? '#EAF2FB' : '#F4F6F8' }}
   onClick={onClick}
  >
   {children}
  </Box>
 );
}

export default FilterCombobox;
